import React from "react";
import { AccountTreeOutlined, Check, InboxOutlined } from "@mui/icons-material";
import { Box, Card, CardContent, Chip, Skeleton, Typography } from "@mui/material";
import { WORKFLOW_CANVAS_LABELS, WorkflowCanvas } from "./workflowBuilderData";

export type WorkflowPipelineStepState = "done" | "current" | "waiting" | "rejected";

const STEP_STATE_LABELS: Record<WorkflowPipelineStepState, string> = {
  done: "Done",
  current: "Current",
  waiting: "Waiting",
  rejected: "Rejected",
};

const SKELETON_STEPS = 4;

interface WorkflowRunRailProps {
  /** Renders skeleton rows while the case is still loading. */
  loading?: boolean;
  reference?: string;
  definitionLabel?: string;
  statusLabel?: string;
  /** BEM modifier for the status chip — "active", "completed", "rejected" or "cancelled". */
  statusModifier?: string;
  stages?: WorkflowCanvas[];
  /** One entry per stage, same order as `stages`. */
  stepStates?: WorkflowPipelineStepState[];
  progressLabel?: string;
}

/** The sticky right rail of the Test / Run screen: the Reference card and the Instance pipeline. */
const WorkflowRunRail: React.FC<WorkflowRunRailProps> = ({
  loading = false,
  reference = "",
  definitionLabel = "",
  statusLabel = "",
  statusModifier = "active",
  stages = [],
  stepStates = [],
  progressLabel = "",
}) => (
  <Box component="aside" className="workflow-run__rail" aria-label="Instance details" aria-busy={loading}>
    <Card className="workflow-run__rail-card" variant="outlined">
      <CardContent>
        <Typography className="workflow-run__rail-title">
          <InboxOutlined aria-hidden="true" />
          Reference
        </Typography>
        {loading ? (
          <>
            <Skeleton variant="text" width="70%" height={28} />
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="rounded" width={88} height={22} />
          </>
        ) : (
          <>
            <Typography className="workflow-run__reference">{reference}</Typography>
            <Typography className="workflow-run__definition">{definitionLabel}</Typography>
            <Chip
              className={`workflow-run__status workflow-run__status--${statusModifier}`}
              label={
                <>
                  <span className="workflow-run__status-dot" aria-hidden="true" />
                  {statusLabel}
                </>
              }
              size="small"
            />
          </>
        )}
      </CardContent>
    </Card>

    <Card className="workflow-run__rail-card" variant="outlined">
      <CardContent>
        <Typography className="workflow-run__rail-title">
          <AccountTreeOutlined aria-hidden="true" />
          Instance pipeline{loading ? "" : ` ${progressLabel}`}
        </Typography>
        {loading ? (
          Array.from({ length: SKELETON_STEPS }, (_, index) => (
            <Box key={index} className="workflow-run__pipeline-step">
              <Skeleton variant="circular" width={24} height={24} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="75%" />
                <Skeleton variant="text" width="45%" />
              </Box>
            </Box>
          ))
        ) : (
          <Box component="ol" className="workflow-run__pipeline" aria-label="Instance pipeline">
            {stages.map((stage, index) => {
              const stepState = stepStates[index] ?? "waiting";
              return (
                <Box
                  component="li"
                  key={stage.id}
                  className={`workflow-run__pipeline-step workflow-run__pipeline-step--${stepState}`}
                >
                  <span className="workflow-run__step-marker">
                    {stepState === "done" ? <Check /> : index + 1}
                  </span>
                  <span className="workflow-run__step-copy">
                    <strong>{stage.name}</strong>
                    <span>
                      {WORKFLOW_CANVAS_LABELS[stage.type]} · {STEP_STATE_LABELS[stepState]}
                    </span>
                  </span>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  </Box>
);

export default WorkflowRunRail;
